import { useState } from 'react';
import { Upload, FileText, Loader, CheckCircle, XCircle, Sparkles, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { InvoicePreview } from './InvoicePreview';
import { parseMaintenanceWithGemini } from '../../lib/geminiParser';

interface LineItem {
  item_type: 'labor' | 'parts';
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface ParsedData {
  maintenanceType?: string;
  serviceProvider?: string;
  providerEmail?: string;
  providerPhone?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  serviceDate?: string;
  unitNumber?: string;
  vin?: string;
  odometer?: string;
  description?: string;
  laborCost?: string;
  partsCost?: string;
  cost?: string;
  companyCostAmount?: string;
  glAccount?: string;
  lineItems?: LineItem[];
}

interface PDFParserProps {
  onDataParsed: (data: ParsedData) => void;
  onFileSelected: (file: File) => void;
}

export function PDFParser({ onDataParsed, onFileSelected }: PDFParserProps) {
  const toast = useToast();
  const [parsing, setParsing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<ParsedData | null>(null);
  const [parseStatus, setParseStatus] = useState<'idle' | 'success' | 'error'>('idle');
  // Object URL + type of the uploaded file, so we can show a visual preview the
  // user can verify the extracted data against.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isImageFile, setIsImageFile] = useState(false);

  const parseWithGemini = async (file: File): Promise<ParsedData> => {
    const aiData = await parseMaintenanceWithGemini(file);

    // Map AI response to ParsedData format
    const parsedData: ParsedData = {};

    if (aiData.maintenance_type) parsedData.maintenanceType = aiData.maintenance_type;
    if (aiData.service_provider) parsedData.serviceProvider = aiData.service_provider;
    if (aiData.provider_email) parsedData.providerEmail = aiData.provider_email;
    if (aiData.provider_phone) parsedData.providerPhone = aiData.provider_phone;
    if (aiData.street) parsedData.street = aiData.street;
    if (aiData.city) parsedData.city = aiData.city;
    if (aiData.state) parsedData.state = aiData.state;
    if (aiData.zip_code) parsedData.zipCode = aiData.zip_code;
    if (aiData.service_date) parsedData.serviceDate = aiData.service_date;
    if (aiData.truck_number) parsedData.unitNumber = aiData.truck_number;
    if (aiData.vin) parsedData.vin = aiData.vin;
    if (aiData.odometer) parsedData.odometer = String(aiData.odometer);
    if (aiData.description) parsedData.description = aiData.description;
    if (aiData.labor_cost != null) parsedData.laborCost = String(aiData.labor_cost);
    if (aiData.parts_cost != null) parsedData.partsCost = String(aiData.parts_cost);
    if (aiData.cost != null) {
      parsedData.cost = String(aiData.cost);
      parsedData.companyCostAmount = String(aiData.cost);
    }
    if (aiData.gl_account) parsedData.glAccount = aiData.gl_account;

    // Map line items
    if (aiData.line_items && Array.isArray(aiData.line_items) && aiData.line_items.length > 0) {
      parsedData.lineItems = aiData.line_items.map((item: any) => ({
        item_type: item.item_type === 'parts' ? 'parts' : 'labor',
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || item.amount || 0,
        amount: item.amount || 0,
      }));
    }

    return parsedData;
  };

  // Confirm: import the (possibly edited) values. The preview edits a subset of
  // fields, so merge them over the full parsed object to keep fields the preview
  // doesn't show (maintenance type, GL account, description, etc.).
  const handlePreviewConfirm = (edited: ParsedData) => {
    setShowPreview(false);
    if (selectedFile) {
      const merged = { ...previewData, ...edited } as ParsedData;
      toast.success('Imported — review the form below and adjust if needed.');
      onDataParsed(merged);
      onFileSelected(selectedFile);
      setParseSuccess(true);
    }
  };

  // Cancel: dismiss without importing, and reset back to the upload state.
  const handlePreviewCancel = () => {
    setShowPreview(false);
    setParseSuccess(false);
    setSelectedFile(null);
    setPreviewData(null);
    setIsImageFile(false);
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleFileSelect = async (file: File) => {
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      toast.error('Please select a PDF or image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    // Build a preview of the file so the user can verify the extraction against
    // the original. Revoke any previous one first to avoid leaks.
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setIsImageFile(isImage);

    setParsing(true);
    setParseStatus('idle');

    try {
      const parsedData = await parseWithGemini(file);

      console.log('=== AI PARSED DATA ===');
      console.log(JSON.stringify(parsedData, null, 2));
      console.log('=== END AI PARSED DATA ===');

      const hasData = Object.keys(parsedData).length > 0;

      if (hasData) {
        setParseStatus('success');
        setPreviewData(parsedData);
        setShowPreview(true);
        setSelectedFile(file);
      } else {
        setParseStatus('error');
        toast.warning('Could not extract data from PDF. Please fill manually.');
        onFileSelected(file);
      }
    } catch (error: any) {
      console.error('Error parsing PDF:', error);
      setParseStatus('error');
      toast.error(error.message || 'Failed to parse PDF. Please fill manually.');
      onFileSelected(file);
    } finally {
      setParsing(false);
    }
  };

  return (
    <>
      {showPreview && previewData && (
        <InvoicePreview
          data={previewData}
          onConfirm={handlePreviewConfirm}
          onClose={handlePreviewCancel}
          fileUrl={previewUrl}
          fileType={isImageFile ? 'image' : 'pdf'}
          fileName={selectedFile?.name}
        />
      )}

    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-dashed border-blue-300 p-6">
      <div className="flex items-start gap-4">
        <div className="bg-blue-600 p-3 rounded-lg">
          <Sparkles className="w-6 h-6 text-white" />
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            AI-Powered Document Parser
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Upload an invoice, receipt, or work order — PDF or image — and we'll automatically extract the maintenance information using Google Gemini!
          </p>

          {!parseSuccess ? (
            <label className="flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-medium">
              <Upload className="w-5 h-5" />
              Upload PDF or Image to Auto-Fill
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
                className="hidden"
              />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                {isImageFile ? (
                  <ImageIcon className="w-8 h-8 text-indigo-600 flex-shrink-0" />
                ) : (
                  <FileText className="w-8 h-8 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {selectedFile?.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                {parsing && (
                  <Loader className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                )}
                {parseStatus === 'success' && (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                )}
                {parseStatus === 'error' && (
                  <XCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                )}
              </div>

              {parseStatus === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    Data extracted successfully! Review the form below and make any adjustments.
                  </p>
                </div>
              )}

              {parseStatus === 'error' && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800 font-medium">
                    Could not extract all data. Please fill in the remaining fields manually.
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setParseSuccess(false);
                  setSelectedFile(null);
                  setParseStatus('idle');
                  setPreviewUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                  });
                  setIsImageFile(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Upload Different File
              </button>
            </div>
          )}
        </div>
      </div>

      {parsing && (
        <div className="mt-4 pt-4 border-t border-blue-200">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Analyzing document with Google Gemini...</span>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
